import React, { useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Layout,
  Menu,
  Avatar,
  Badge,
  Tooltip,
  theme as antdTheme,
  Grid,
  Drawer,
  Button,
} from "antd";
import type { MenuProps } from "antd";
import { useAppContext } from "../context/AppContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import usePagePermission from "../hooks/usePagePermission";
import {
  UserOutlined,
  MenuOutlined,
  LogoutOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  SendOutlined,
  SettingOutlined,
  BarChartOutlined,
  DollarOutlined,
  ReconciliationOutlined,
  FormOutlined,
  TableOutlined,
} from "@ant-design/icons";

import lightLogo from "../static/cdrpl.png";
import darkLogo from "../static/cdrpl.png";

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
  collapsible: boolean;
  isMobile?: boolean;
  isSmallDesktop?: boolean;
}

const PurchaseNavigation = React.memo(() => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmallDesktop = screens.md && !screens.lg;
  return (
    <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />
  );
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
  const location = useLocation();
  const { state: stateApp, appContextAction } = useAppContext();
  const { isNavMenuClose } = stateApp;
  const { navMenu } = appContextAction;
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
  const logoPath = isDarkMode ? darkLogo : lightLogo;

  const [drawerVisible, setDrawerVisible] = useState(false);

  const canReadPurchase = usePagePermission("purchase", "read");
  const canManageTemplates = usePagePermission("purchase-templates", "write");
  const canReadBom = usePagePermission("purchase-bom", "read");
  //const canReadCostComparison = usePagePermission("cost-comparison", "read");
  //const canManageDesignDocs = usePagePermission("purchase-design-docs", "write");

  const getThemeStyles = useMemo(
    () => ({
      sidebarHeader: {
        padding: isNavMenuClose && !isMobile ? "8px" : "0 16px",
        display: "flex",
        flexDirection: isNavMenuClose && !isMobile ? "column" as const : "row" as const,
        alignItems: "center",
        justifyContent: isNavMenuClose && !isMobile ? "center" : "space-between",
        overflow: "hidden",
        transition: "all 0.3s ease",
        borderBottom: "none",
        backgroundColor: "transparent",
      },
      logoContainer: {
        display: "flex",
        alignItems: "center",
        paddingBottom: isNavMenuClose && !isMobile ? "8px" : "0",
        justifyContent: isNavMenuClose && !isMobile ? "center" : "flex-start",
        marginBottom: isNavMenuClose && !isMobile ? "8px" : "0",
      },
      logo: {
        width: isNavMenuClose && !isMobile ? "40px" : isSmallDesktop ? "140px" : "160px",
        height: "auto",
        transition: "width 0.3s ease",
      },
      collapsedProfile: {
        padding: "16px 0",
        display: "flex",
        justifyContent: "center",
        borderBottom: "none",
        marginBottom: "8px",
        backgroundColor: "transparent",
      },
      navigationMenu: {
        flex: 1,
        overflowY: "auto" as const,
        overflowX: "hidden" as const,
        padding: "0",
        height: "auto",
        minHeight: 0,
      },
      sidebarStyle: {
        boxShadow: "none",
        zIndex: 1000,
        height: "100vh",
        position: "fixed" as const,
        left: 0,
        display: "flex",
        flexDirection: "column" as const,
        transition: "all 0.2s ease",
        background: "transparent",
        backgroundColor: "transparent",
        overflow: "hidden",
        ...(isSmallDesktop && !isNavMenuClose && {
          width: "180px !important",
        }),
      },
    }),
    [isNavMenuClose, isMobile, isSmallDesktop]
  );

  const currentPath = useMemo(() => {
    const path = location.pathname;

    if (path === "/purchase/orders" || path === "/purchase-stage/orders") return "orders";
    if (path.startsWith("/purchase/purchase-design-docs")) return "design-docs";
    if (path.startsWith("/purchase/purchase-bom")) return "purchase-bom";
    if (path.startsWith("/purchase/material-tracking")) return "material-tracking";
    if (path.startsWith("/purchase/cost-comparison")) return "cost-comparison";
    if (path.startsWith("/purchase/cost-sheet")) return "cost-sheet";
    if (path.startsWith("/purchase/templates")) return "templates";
    if (path.startsWith("/purchase/dispatches")) return "dispatches";
    if (path.startsWith("/purchase/input-form")) return "input-form";
    if (path.startsWith("/purchase/settings")) return "settings";

    const segments = path.split("/").filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : "";
  }, [location.pathname]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [];

    // if (canReadPurchase) {
    //   items.push({
    //     key: "orders",
    //     icon: <ShoppingCartOutlined />,
    //     label: <Link to="/purchase/orders">Purchase Orders</Link>,
    //   });
    // }

    items.push({
       key: "design-docs",
       icon: <FileTextOutlined />,
       label: <Link to="/purchase/purchase-design-docs">Design Documents</Link>,
    })

    if (canReadBom) {
      items.push({
        key: "purchase-bom",
        icon: <BarChartOutlined />,
        label: <Link to="/purchase/purchase-bom">Bill Of Material</Link>,
      });
    }
    
    if (canReadBom) {
      items.push({
        key: "cost-comparison",
        icon: <DollarOutlined />,
        label: <Link to="/purchase/cost-comparison">Cost Comparison</Link>,
      });
    }

    if (canReadBom) {
      items.push({
        key: "material-tracking",
        icon: <ReconciliationOutlined />,
        label: <Link to="/purchase/material-tracking">Material Tracking</Link>,
      });
    }

    if (canReadBom) {
      items.push({
        key: "cost-sheet",
        icon: <TableOutlined />,
        label: <Link to="/purchase/cost-sheet">Cost Sheet</Link>,
      });
    }

    // if (canReadDispatches) {
      items.push({
        key: "dispatches",
        icon: <SendOutlined />,
        label: <Link to="/purchase/dispatches">Dispatches</Link>,
      });
    // }

    items.push({
      key: "input-form",
      icon: <FormOutlined />,
      label: <Link to="/purchase/input-form">Input Form</Link>,
    });

    items.push({
      key: "settings",
      icon: <SettingOutlined />,
      label: <Link to="/purchase/settings">Settings</Link>,
    });

    if (canReadPurchase || canManageTemplates) {
      items.push({ type: "divider" });
    }

    items.push({
      key: "back-to-stages",
      icon: <ArrowLeftOutlined />,
      label: <Link to="/stage-selection">Back to Stages</Link>,
    });

    items.push({
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: logout,
      danger: true,
    });

    return items;
  }, [canReadPurchase, canReadBom, canManageTemplates, logout]);

  const toggleCollapse = useCallback(() => {
    if (isNavMenuClose) {
      navMenu.expand();
    } else {
      navMenu.collapse();
    }
  }, [isNavMenuClose, navMenu]);

  const handleMenuClick = useCallback(
    (info: any) => {
      if (info.key === "logout") {
        logout();
      }

      if (isMobile) {
        setDrawerVisible(false);
      }
    },
    [logout, isMobile]
  );

  const toggleDrawer = useCallback(() => {
    setDrawerVisible((prev) => !prev);
  }, []);

  const SidebarContent = useMemo(
    () => (
      <div
        className="transparent-nav"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
          <div className="logo-container" style={getThemeStyles.logoContainer}>
            <img src={logoPath} alt="CDRPL" style={getThemeStyles.logo} />
          </div>
        </div>

        {isNavMenuClose && !isMobile && (
          <div style={getThemeStyles.collapsedProfile}>
            <Tooltip title={user?.name || "User"} placement="right">
              <Badge dot status="success" offset={[-4, 28]}>
                <Avatar icon={<UserOutlined />} size={36} />
              </Badge>
            </Tooltip>
          </div>
        )}

        <div className="navigation-menu" style={getThemeStyles.navigationMenu}>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            defaultOpenKeys={[]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: "none",
              background: "transparent",
              height: "100%",
            }}
            theme={isDarkMode ? "dark" : "light"}
          />
        </div>
      </div>
    ),
    [
      getThemeStyles,
      logoPath,
      isNavMenuClose,
      isMobile,
      user,
      currentPath,
      menuItems,
      handleMenuClick,
      isDarkMode,
    ]
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          shape="circle"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{
            position: "fixed",
            top: "16px",
            left: "16px",
            zIndex: 999,
            boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
          }}
          size="middle"
        />
        <Drawer
          placement="left"
          onClose={toggleDrawer}
          open={drawerVisible}
          width={320}
          styles={{
            body: {
              padding: 0,
              height: "100%",
              background: "transparent",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          }}
          closable={false}
        >
          {SidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      collapsible={collapsible}
      collapsed={isNavMenuClose}
      onCollapse={toggleCollapse}
      className="navigation-sidebar transparent-nav"
      theme={isDarkMode ? "dark" : "light"}
      width={isSmallDesktop ? 180 : 220}
      collapsedWidth={70}
      style={getThemeStyles.sidebarStyle}
      trigger={null}
    >
      {SidebarContent}
    </Sider>
  );
});

export default PurchaseNavigation;
